import { useId } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { BOLD_TEXT, COLOR_TEXT, SIZE_TEXT } from "@/comn/features/foundation";
import { comnUtils } from "@/comn/utils";
import { useOptions_v2 } from "@/comn/hooks/useOptions.v2";
import { UseOptionsProps } from "@/comn/hooks";

type RadioProps = UseOptionsProps & {
    all?: boolean;
    edit?: boolean;
    viewType?: "label" | "value" | "both";
    editType?: "label" | "value" | "both";
    mode?: "edit" | "view" | null;

    name?: string;
    value?: any;
    readOnly?: boolean;
    disabled?: boolean;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;
    align?: "start" | "left" | "center" | "right" | "end";

    onBlur?: (arg?: any) => void;
    onChange?: (arg?: any) => void;
    callback?: (arg?: any) => void;
};

export const Radio = (props: RadioProps) => {
    const {
        all,
        edit = true,
        mode,
        viewType = "both",
        editType = "label",
        value,
        readOnly,
        disabled,
        align,
        onBlur,
        onChange,
        // For style
        color,
        bold,
        editColor,
        editBold,
        fontSize,
        // For useOptions
        options: propsOptions,
        area,
        comnCd,
        excludes,
        includes,
        filter,
    } = props;

    const _props = Object.fromEntries(
        Object.entries({
            /** */
            onBlur,
        }).filter(([, value]) => value !== undefined),
    );

    const { t } = useTranslation();
    const { options, getListItemKey } = useOptions_v2({
        propsOptions,
        area,
        comnCd,
        excludes,
        includes,
        filter,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        if (onChange) {
            onChange(nextValue);
        }
    };

    const hiddenInput = mode === "view" ? true : mode === "edit" ? false : !edit;
    const formattedValue = formatRadio(value);
    const viewValue = t(viewRadio(value, { viewType, options }));

    return (
        <div className="w-full">
            {/* Text */}
            {hiddenInput && (
                <div
                    title={viewRadio(value, { options, viewType }, true)}
                    className={classNames("w-fit", comnUtils.getViewStyle(color, bold, fontSize))}
                >
                    {viewValue}
                </div>
            )}

            {/* Input */}
            <div hidden={hiddenInput}>
                <div
                    className={classNames("flex flex-wrap w-fit", readOnly && "pointer-events-none", {
                        "justify-center": align === "center",
                        "justify-start": align === "left",
                        "justify-end": align === "right",
                    })}
                >
                    {all && (
                        <label className="flex items-center h-7 space-x-1 mr-3">
                            <input
                                {..._props}
                                type="radio"
                                value=""
                                checked={formattedValue === ""}
                                onChange={handleChange}
                            />
                            <div className={comnUtils.getEditStyle(editColor, editBold)}>{t(`L_AL`)}</div>
                        </label>
                    )}

                    {options.map((item, index) => {
                        const { value, label } = item;
                        return (
                            <label key={getListItemKey(item, index)} className="flex items-center h-7 space-x-1 mr-3">
                                <input
                                    {..._props}
                                    type="radio"
                                    value={value}
                                    checked={value === formattedValue}
                                    readOnly={readOnly && value === formattedValue}
                                    disabled={disabled || (readOnly && value !== formattedValue)}
                                    onChange={handleChange}
                                />
                                {label && (
                                    <div className={comnUtils.getEditStyle(editColor, editBold)}>
                                        {editRadio(value, t(label), editType)}
                                    </div>
                                )}
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export const viewRadio = (v: any, o?: any, all?: boolean) => {
    if (!o?.options) return;

    const option = o.options?.find(({ value }: any) => value === v);

    if (!option) return;

    const vt = option.value || "";
    const lt = option.label || "";
    const vtWithBracket = option.value ? `[${option.value}] ` : "";

    let view = o?.viewType === "value" ? vt : o?.viewType === "label" ? lt : vtWithBracket + lt;
    if (all === true) {
        view = vtWithBracket + lt;
    }
    return view;
};

export const editRadio = (value: string, label: string, editType: string) => {
    const view = editType === "label" ? label : editType === "value" ? value : "[" + value + "] " + label;
    return view;
};

export const formatRadio = (v: any) => {
    if (!v) return "";
    return String(v);
};

export const unformatRadio = (v: any, o?: any) => {
    if (v) return undefined;
    return String(v);
};
